import React, { useState, useCallback, useRef, useEffect } from "react"
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material"
import PlayArrowIcon from "@mui/icons-material/PlayArrow"
import StopIcon from "@mui/icons-material/Stop"
import VideocamIcon from "@mui/icons-material/Videocam"
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord"
import useCameraStream from "../../hooks/useCameraStream"
import warpPerspective from "../../utils/warpPerspective"

const ProjectCamera = () => {
  const [invHomography, setInvHomography] = useState(null)
  const [overlayUrl, setOverlayUrl] = useState(null)
  const [error, setError] = useState(null)
  const [opacity, setOpacity] = useState(0.5)
  const [captureIntervalMs, setCaptureIntervalMs] = useState(250)
  const [diffThreshold, setDiffThreshold] = useState(0)
  const [diffMode, setDiffMode] = useState("last") // "last" | "first"
  const kilterImgRef = useRef(null)
  const filterCanvasRef = useRef(null)
  const prevFrameRef = useRef(null)

  // Pre-load the kilter image once
  useEffect(() => {
    const img = new Image()
    // img.src = "/images/kilter.jpg"
    img.src = "images/kilter-color-holds.png"
    img.onload = () => {
      kilterImgRef.current = img
    }
  }, [])

  // Warp the kilter image whenever we get a new inverse homography
  useEffect(() => {
    if (!invHomography || !kilterImgRef.current) return
    const video = videoRef.current
    if (!video) return
    const w = video.videoWidth || 640
    const h = video.videoHeight || 480
    // The server computed h_square_to_query against a frame downscaled so that
    // its largest axis is 1024 (see downscale() in utils/imageFunctions.js).
    // Scale it back up to the original video resolution.
    const s = Math.max(w, h) / 1024
    const H = invHomography
    const scaledHomography = [
      [H[0][0] * s, H[0][1] * s, H[0][2] * s],
      [H[1][0] * s, H[1][1] * s, H[1][2] * s],
      [H[2][0],     H[2][1],     H[2][2]],
    ]
    const warped = warpPerspective(kilterImgRef.current, scaledHomography, w, h)
    setOverlayUrl(warped.toDataURL("image/png"))
  }, [invHomography])

  const fetchHomography = useCallback(
    async (file, { setLoading, loadingRef }) => {
      setLoading(true)
      loadingRef.current = true
      const formData = new FormData()
      formData.append("image", file)

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL || ""}/api/find_homography`,
          {
            method: "POST",
            body: formData,
          }
        )
        const json = await response.json()
        if (json.error) {
          setError(json.error)
          setInvHomography(null)
          setOverlayUrl(null)
        } else {
          setInvHomography(json.h_square_to_query)
          setError(null)
        }
      } catch (err) {
        console.error(err)
        setError(err.message)
      } finally {
        setLoading(false)
        loadingRef.current = false
      }
    },
    []
  )

  const {
    videoRef,
    canvasRef,
    devices,
    selectedDeviceId,
    handleDeviceChange,
    isCapturing,
    toggleCapture,
    loading,
    videoSize,
  } = useCameraStream({ onFile: fetchHomography, captureIntervalMs })

  // Frame-difference filter — subtract a reference frame from the current
  // frame (per channel, positive differences only) and zero out any pixel
  // whose largest channel diff is below `diffThreshold`. The reference is
  // either the previous frame ("last") or the first frame captured after
  // the filter was enabled ("first").
  useEffect(() => {
    if (diffThreshold === 0) {
      prevFrameRef.current = null
      return
    }
    // Reset whenever the mode changes so "first" captures a fresh reference.
    prevFrameRef.current = null
    let rafId
    const tick = () => {
      const video = videoRef.current
      const canvas = filterCanvasRef.current
      if (video && canvas && video.videoWidth) {
        if (canvas.width !== video.videoWidth) {
          canvas.width = video.videoWidth
          canvas.height = video.videoHeight
          prevFrameRef.current = null
        }
        const ctx = canvas.getContext("2d", { willReadFrequently: true })
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
        const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height)
        const data = imgData.data
        const prev = prevFrameRef.current
        if (prev && prev.length === data.length) {
          // In "last" mode we need to stash this frame for next tick before
          // we mutate `data`. In "first" mode the reference never changes.
          const currentSnapshot =
            diffMode === "last" ? new Uint8ClampedArray(data) : null
          for (let i = 0; i < data.length; i += 4) {
            const dr = Math.max(0, data[i] - prev[i])
            const dg = Math.max(0, data[i + 1] - prev[i + 1])
            const db = Math.max(0, data[i + 2] - prev[i + 2])
            if (Math.max(dr, dg, db) < diffThreshold) {
              data[i] = 0
              data[i + 1] = 0
              data[i + 2] = 0
            } else {
              data[i] = dr
              data[i + 1] = dg
              data[i + 2] = db
            }
          }
          ctx.putImageData(imgData, 0, 0)
          if (currentSnapshot) prevFrameRef.current = currentSnapshot
        } else {
          // First frame: nothing to diff against, just stash and show black.
          prevFrameRef.current = new Uint8ClampedArray(data)
          for (let i = 0; i < data.length; i += 4) {
            data[i] = 0
            data[i + 1] = 0
            data[i + 2] = 0
          }
          ctx.putImageData(imgData, 0, 0)
        }
      }
      rafId = requestAnimationFrame(tick)
    }
    rafId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafId)
  }, [diffThreshold, diffMode, videoRef])

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 3,
      }}
    >
      {/* Controls bar */}
      <Card sx={{ width: "100%", maxWidth: 640 }}>
        <CardContent
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            p: 2,
            "&:last-child": { pb: 2 },
            flexWrap: "wrap",
          }}
        >
          <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
            <InputLabel>Camera</InputLabel>
            <Select
              value={selectedDeviceId}
              onChange={handleDeviceChange}
              label="Camera"
            >
              {devices.map((device) => (
                <MenuItem key={device.deviceId} value={device.deviceId}>
                  {device.label || `Camera ${device.deviceId.slice(0, 8)}...`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Interval</InputLabel>
            <Select
              value={captureIntervalMs}
              onChange={(e) => setCaptureIntervalMs(e.target.value)}
              label="Interval"
            >
              <MenuItem value={100}>100 ms</MenuItem>
              <MenuItem value={250}>250 ms</MenuItem>
              <MenuItem value={500}>500 ms</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant={isCapturing ? "outlined" : "contained"}
            onClick={toggleCapture}
            startIcon={isCapturing ? <StopIcon /> : <PlayArrowIcon />}
            color={isCapturing ? "error" : "primary"}
            sx={{ minWidth: 120 }}
          >
            {isCapturing ? "Stop" : "Start"}
          </Button>

          {isCapturing && (
            <FiberManualRecordIcon
              sx={(theme) => ({
                color: theme.palette.error.main,
                fontSize: 14,
                animation: "pulse 1.5s ease-in-out infinite",
                "@keyframes pulse": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.3 },
                },
              })}
            />
          )}
        </CardContent>
      </Card>

      {/* Video feed with overlay */}
      <Card
        sx={{
          width: "100%",
          maxWidth: videoSize.width || 640,
          overflow: "hidden",
        }}
      >
        <Box sx={{ position: "relative", lineHeight: 0 }}>
          <Box
            component="video"
            ref={videoRef}
            autoPlay
            playsInline
            muted
            sx={{
              width: "100%",
              display: "block",
              bgcolor: "background.default",
              minHeight: 200,
            }}
          />

          {diffThreshold > 0 && (
            <Box
              component="canvas"
              ref={filterCanvasRef}
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "none",
              }}
            />
          )}

          {overlayUrl && (
            <Box
              component="img"
              src={overlayUrl}
              alt="Kilter overlay"
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "fill",
                opacity: opacity,
                pointerEvents: "none",
              }}
            />
          )}

          {!isCapturing && !overlayUrl && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                color: "text.secondary",
                bgcolor: "rgba(0,0,0,0.5)",
              }}
            >
              <VideocamIcon sx={{ fontSize: 56, opacity: 0.5 }} />
              <Typography variant="body1">
                Select a camera and press Start
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.5 }}>
                Kilter board overlay will appear here
              </Typography>
            </Box>
          )}
        </Box>

        {overlayUrl && (
          <Box sx={{ px: 3, py: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1, fontSize: "0.8rem" }}
            >
              Overlay opacity
            </Typography>
            <Slider
              value={opacity}
              onChange={(e, v) => setOpacity(v)}
              min={0}
              max={1}
              step={0.01}
              size="small"
            />
          </Box>
        )}

        <Box sx={{ px: 3, py: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.8rem" }}
            >
              Frame-diff threshold ({diffThreshold})
            </Typography>
            <ToggleButtonGroup
              value={diffMode}
              exclusive
              size="small"
              onChange={(e, v) => {
                if (v) setDiffMode(v)
              }}
            >
              <ToggleButton value="last" sx={{ py: 0.25, px: 1 }}>
                Last frame
              </ToggleButton>
              <ToggleButton value="first" sx={{ py: 0.25, px: 1 }}>
                First frame
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Slider
            value={diffThreshold}
            onChange={(e, v) => setDiffThreshold(v)}
            min={0}
            max={255}
            step={1}
            size="small"
          />
        </Box>
      </Card>

      <canvas ref={canvasRef} style={{ display: "none" }} />

      {/* Status indicators */}
      {loading && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            color: "text.secondary",
          }}
        >
          <CircularProgress size={20} />
          <Typography variant="body2">Computing homography...</Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ width: "100%", maxWidth: 640 }}>
          {error}
        </Alert>
      )}
    </Box>
  )
}

export default ProjectCamera
